// Theme-driven select — wraps the shared anchored dropdown (topbar/dropdown) in
// the list-button shell (`.mz-lstv_s`) so it drops into a binding row's value
// cell. The `options` map mirrors the pane's addBinding `{ Label: value }`
// shape. Pure DOM; lifts into webpic's @webpic/ui/controls.

import { createDropdown } from '../../topbar/dropdown';
import type { DropdownHandle, DropdownOption } from '../../topbar/dropdown';
import type { Disposer } from '../../types';

export interface SelectHandle {
    el: HTMLButtonElement;
    /** Re-read the bound value, refresh the trigger label + any open popover. */
    sync(): void;
    setDisabled(disabled: boolean): void;
    dispose: Disposer;
}

export function createSelect(opts: {
    options: Record<string, unknown>;
    getValue: () => unknown;
    onChange: (value: string) => void;
}): SelectHandle {
    // Option maps are label → value; the popover shows labels but
    // commits the (stringified) value, which every change handler expects.
    const entries: DropdownOption[] = Object.entries(opts.options).map(([label, value]) => ({ label, value: String(value) }));

    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'mz-lstv_s';

    const labelFor = (value: string): string => entries.find((e) => e.value === value)?.label ?? value;
    const syncLabel = (): void => { trigger.textContent = labelFor(String(opts.getValue())); };
    syncLabel();

    const dropdown: DropdownHandle = createDropdown({
        trigger,
        getOptions: () => entries,
        getSelected: () => String(opts.getValue()),
        onSelect: (value) => { opts.onChange(value); syncLabel(); },
    });

    return {
        el: trigger,
        sync() { syncLabel(); dropdown.refresh(); },
        setDisabled(disabled) { trigger.disabled = disabled; },
        // The trigger is removed with the row; only the body-level popover needs
        // explicit teardown.
        dispose() { dropdown.dispose(); },
    };
}
